import React, { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { useLocation, useParams } from "react-router-dom";
import MFHoldingsTable from "../../components/HoldingsTable/MFHoldingsTable";
import HoldingsErrorDialog from "../../components/HoldingsTable/HoldingsErrorDialog";
import AppLoader from "../../components/AppLoader/AppLoader";
import { getMF, getMFHoldings } from "../../redux/mf/mfSlice";
import { getUserById } from "../../redux/user/userSlice";
import {
  extractHoldingsList,
  extractHoldingsPagination,
  getHoldingsErrorMessage,
  HoldingsApiPayload,
  isHoldingsApiError,
} from "../../utils/holdingsApiUtils";

interface MF {
  id: number;
  folioNo: string;
  schemeName: string;
  investmentDate: string;
  amount: number;
  purchaseNav: number;
  units: number;
  currentNav: number;
  currentDate: string;
  currentVal: number;
  gainNloss: number | null;
  absReturn: string | null;
  xirr: string;
  createdAt: string;
  noOfDays: string;
}

const MFHoldings: React.FC = () => {
  const [loader, setLoader] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const dispatch = useDispatch<any>();
  const location = useLocation();
  const [MF, setMF] = useState<MF[]>([]);
  const [pageSize, setPageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);
  const { id } = useParams();
  const userTypeRef = useRef<number | undefined>(
    (location.state as { userType?: number } | null)?.userType
  );
  const fetchInFlightRef = useRef(false);

  const mapMFHolding = (holding: any): MF => {
    const firstTxn = holding?.transactions?.[0];
    const investmentDate = firstTxn?.transactionDate
      ? new Date(Number(firstTxn.transactionDate)).toLocaleDateString("en-GB")
      : holding?.investmentDate
        ? new Date(Number(holding.investmentDate)).toLocaleDateString("en-GB")
        : "-";

    return {
      id: holding.id,
      folioNo: holding.folioNumber ?? holding.folioNo ?? "-",
      schemeName: holding.schemeName ?? "-",
      investmentDate,
      amount: holding.amount ?? holding.costValue ?? holding.invested ?? 0,
      purchaseNav: holding.averageNav ?? holding.purchaseNav ?? 0,
      units: holding.units ?? 0,
      currentNav: holding.currentNAV ?? holding.currentNav ?? 0,
      currentDate: new Date().toLocaleDateString("en-GB"),
      currentVal: holding.currentValue ?? holding.currentVal ?? 0,
      gainNloss:
        holding.unrealisedPNL ?? holding.gainNloss ?? holding.gainLoss ?? null,
      absReturn:
        holding.unrealisedPNLPercentage != null
          ? String(holding.unrealisedPNLPercentage)
          : holding.absReturn != null
            ? String(holding.absReturn)
            : null,
      xirr: String(holding.xirr ?? "-"),
      createdAt: holding.updatedAt ?? "",
      noOfDays: String(firstTxn?.days ?? holding.noOfDays ?? "-"),
    };
  };

  const resolveUserType = useCallback(async (): Promise<number | undefined> => {
    if (userTypeRef.current !== undefined) return userTypeRef.current;

    const storedType = sessionStorage.getItem(`holdings-user-type-${id}`);
    if (storedType !== null && storedType !== "") {
      const parsed = Number(storedType);
      userTypeRef.current = parsed;
      return parsed;
    }

    try {
      const userRes = await dispatch(getUserById({ userId: Number(id) }));
      const userType = userRes.payload?.data?.[0]?.userType;
      userTypeRef.current = userType;
      if (userType !== undefined) {
        sessionStorage.setItem(`holdings-user-type-${id}`, String(userType));
      }
      return userType;
    } catch (error) {
      console.error("Error resolving user type:", error);
      return undefined;
    }
  }, [dispatch, id]);

  const fetchMF = useCallback(async () => {
    if (fetchInFlightRef.current) return;

    fetchInFlightRef.current = true;
    setLoader(true);

    try {
      const accountUserType = await resolveUserType();
      const userId = Number(id);

      const response = await dispatch(
        accountUserType === 1
          ? getMF({
              userId,
              startDate: 1,
              endDate: 1,
              fundType: "",
              transactionType: 1,
              fetchFor: "consolidated",
            })
          : getMFHoldings({
              userId,
              startDate: 0,
              endDate: 0,
            })
      );

      if (getMF.rejected.match(response) || getMFHoldings.rejected.match(response)) {
        setErrorMessage(getHoldingsErrorMessage(response.payload));
        setMF([]);
        return;
      }

      const payload = response.payload as HoldingsApiPayload;
      if (isHoldingsApiError(payload)) {
        setErrorMessage(getHoldingsErrorMessage(payload));
        setMF([]);
        return;
      }

      const mappedHoldings = extractHoldingsList(payload).map(mapMFHolding);
      const pagination = extractHoldingsPagination(payload);
      setMF(mappedHoldings);
      setTotalPages(pagination.totalPages);
      setCurrentPage(pagination.pageIndex);
    } catch (error) {
      console.error("Error fetching MF data:", error);
      setErrorMessage("Failed to load mutual fund holdings.");
      setMF([]);
    } finally {
      fetchInFlightRef.current = false;
      setLoader(false);
    }
  }, [dispatch, id, resolveUserType]);

  useEffect(() => {
    fetchMF();
  }, [fetchMF]);

  const handlePageChange = (
    _event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number
  ) => {
    setCurrentPage(newPage);
  };

  const handlePageSizeChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setPageSize(parseInt(event.target.value, 10));
    setCurrentPage(0);
  };

  return (
    <>
      <AppLoader loading={loader} />
      <MFHoldingsTable
        data={MF}
        totalPages={totalPages}
        currentPage={currentPage}
        pageSize={pageSize}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
        fetchData={fetchMF}
      />
      <HoldingsErrorDialog
        open={Boolean(errorMessage)}
        message={errorMessage}
        title="Unable to load mutual fund holdings"
        onClose={() => setErrorMessage(null)}
      />
    </>
  );
};

export default MFHoldings;
